<template>
  <div
    v-if="isPanelsDontNeed"
    id="wrapper"
    class="wrapper"
  >
    <RouterView />
    <ChatPanel
      v-if="isChatVisible"
      :name="$t('chat.title')"
      :visible="isChatVisible"
      @close="setChatVisible(false)"
    />
  </div>

  <GlobalLoader v-else-if="loading" />

  <div
    v-else
    class="app"
  >
    <AuAiChat v-if="isAiChat" />
    <GlobalError
      v-if="errorText"
      :header="$t('globalError.header')"
      :body="errorText"
      width="500px"
    />

    <div class="app__left">
      <LeftPanel />
    </div>

    <div class="app__right">
      <TopPanel />
      <RouterView />
      <ChatPanel
        v-if="isChatVisible"
        :name="$t('chat.title')"
        :visible="isChatVisible"
        @close="setChatVisible(false)"
      />
    </div>
  </div>

  <CookieBanner v-if="!isCarrotEnabled" />
</template>

<script>
import AuAiChat from "@/components/extended/AuAIChat";
import ChatPanel from "@/components/ChatPanel";
import CookieBanner from "@/components/uikit/platformComponents/CookieBanner.vue";
import GlobalError from "@/components/GlobalError.vue";
import GlobalLoader from "@/components/GlobalLoader.vue";
import isUserRole from "@/help/isUserRole";
import LeftPanel from "@/panels/LeftPanel.vue";
import localStorageController from "@/help/localStorageController";
import TopPanel from "@/panels/TopPanel.vue";

import { createWeb3Modal, defaultConfig } from "@web3modal/ethers5/vue";
import { mapActions, mapGetters } from "vuex";
import { startIntervalRefreshToken } from "@/help/tokenLife";

import _ from "lodash";
import { checkAccess, scope } from "@/help/accessData";

export default {
  name: "App",

  components: {
    AuAiChat,
    ChatPanel,
    LeftPanel,
    TopPanel,
    GlobalLoader,
    GlobalError,
    CookieBanner
  },

  data() {
    return {
      loading: true,
      socketInterval: null
    };
  },

  computed: {
    ...mapGetters({
      isLightTheme: "isLightTheme",
      isChatVisible: "isChatVisible",
      tokens: "tokens",
      tokensGold: "tokensGold",
      defaultTokenId: "defaultToken/defaultTokenId",
      defaultAutMnemonic: "defaultToken/defaultAutMnemonic",
      profile: "profile/profile",
      socketStatus: "socket/socketStatus",
      socketHasPreviousConnection: "socket/socketHasPreviousConnection"
    }),

    isIssuer() {
      return isUserRole() === "issuer";
    },

    isAiChat() {
      return process.env.VUE_APP_ENABLE_ADVISER == "true";
    },

    isCarrotEnabled() {
      return process.env.VUE_APP_CARROT_ENABLED == "true";
    },

    errorText() {
      if (!this.defaultTokenId) {
        return this.$t("globalError.defaultTokenBody");
      }
      else if (!this.defaultAutMnemonic) {
        return this.$t("globalError.defaultAutMnemonicBody");
      }

      return null;
    },

    isOuter() {
      return this.$route.meta.outer == true;
    },

    isPanelsDontNeed() {
      return (
        !this.$auth.user() ||
        this.$route?.name === "logout" ||
        this.$route?.name === "login" ||
        this.$route?.name === "page not found" ||
        this.$route?.name === "EmailVerify" ||
        this.$route?.meta.outer === true
      );
    },

    user() {
      return this.$auth.user();
    },

    defaultToken() {
      return _.find(this.tokens, {
        mnemonicEng: process.env.VUE_APP_TOKEN_DEFAULT
      });
    },

    availableThemes() {
      return process.env.VUE_APP_AVAILABLE_THEMES
        ? process.env.VUE_APP_AVAILABLE_THEMES.split(",")
        : ["light", "dark"];
    },

    isLightThemeDefault() {
      return this.availableThemes.includes("light") && process.env.VUE_APP_THEME_DEFAULT == "light";
    }
  },

  watch: {
    isLightTheme: {
      async handler(newValue) {
        this.setupCarrot();
        document.documentElement.classList = [
          newValue ? "app-light" : "app-dark",
          "theme-cabinet"
        ];
      },
      immediate: true
    },

    socketStatus(status) {
      if (status) {
        if (this.socketHasPreviousConnection) {
          // this.$toast.success("WebSocket re-connected", { showInMenu: false });
          console.log("WebSocket re-connected");
        }
        this.setSocketHasPreviousConnection(true);

        // Останавливаем интервал, если он был запущен иначе будет цикл
        if (this.socketInterval) {
          clearInterval(this.socketInterval);
          this.socketInterval = null;
        }
      }
      else {
        this.getUserBalanceInterval();
        console.error("WebSocket connection error");
      }
    },

    user: {
      immediate: true,
      async handler(newValue, oldValue) {
        if (newValue) {
          if (newValue?.id !== oldValue?.id) {
            this.loading = true;
            await this.getAuTokens({ type: "utility,fiat" });
            this.setDefaultToken({
              id: newValue.defaultTokenId || this.defaultToken?.id,
              mnemonic:
                newValue.defaultTokenMnemonic || this.defaultToken?.mnemonicEng
            });

            this.initializeUnits();
            await this.getProfile(newValue?.id);
            await this.getBalances(this.defaultTokenId);
            await this.getEthAddresses();
            await this.getTonAddresses();
            await this.getSmartContractsTypes();
            await this.getExchangeBalances(newValue?.id);
            await this.getDailyStats();
            await this.getTransactions(this.$auth.user().id);

            if (isUserRole() === "issuer") {
              await this.getHydraRequestToIssue({ page: 1 });
              await this.getHydraCompanies({ page: 1, itemsPerPage: 30 });
            }

            if (!checkAccess(scope.notRwa)) {
              await this.getInvestments();
            }

            if (!this.profile?.carrotId) {
              await this.setUserCarrotId(newValue?.id);
            }

            try {
              startIntervalRefreshToken();
            }
            catch (error) {
              console.error("startIntervalRefreshToken", error);
            }

            try {
              if (
                window.carrotquest &&
                process.env.VUE_APP_CARROT_ENABLED === "true"
              ) {
                window.carrotquest.identify([
                  {
                    op: "update_or_create",
                    key: "$email",
                    value: this.profile.email
                  }
                ]);
              }
            }
            catch (error) {
              console.error(error);
            }

            if (newValue.type === "issuer") {
              await this.getHydraRequestToIssue({ page: 1 });
              await this.getHydraCompanies({ page: 1, itemsPerPage: 30 });
            }

            this.$auth.remember(this.profile.email);

            this.loading = false;
            this.setupCarrot();
            this.windowResize();
          }
        }

        if (this.isOuter) {
          this.setIsLightTheme(true);
        }
        else if (!localStorageController.getUserMadeChoice()) {
          this.setIsLightTheme(this.isLightThemeDefault);
        }
        else {
          let isLightTheme = localStorageController.getIsLightTheme();
          isLightTheme = this.availableThemes.includes(isLightTheme === "true" ? "light" : "dark")
            ? isLightTheme
            : this.isLightThemeDefault;
          this.setIsLightTheme(!(isLightTheme === "false"));
        }

        if (checkAccess(scope.notRwa)) {
          if (!process.env.VUE_APP_SUPPORTED_LOCALES.includes(",")) {
            this.setLanguage(process.env.VUE_APP_SUPPORTED_LOCALES);
          }
        }

        await this.getSystemSettings();
        this.initializeUnits();
      }
    }
  },

  async created() {
    document.documentElement.classList = "theme-cabinet";
    this.initWeb3Modal();
  },

  async mounted() {
    await this.getSystemSettings();
    window.addEventListener("resize", this.windowResize);
  },

  methods: {
    ...mapActions({
      getAuTokens: "getAuTokens",
      getAuOwners: "getAuOwners",
      getBalances: "getBalances",
      getEthAddresses: "getEthAddresses",
      getTonAddresses: "getTonAddresses",
      setDefaultToken: "defaultToken/setDefaultToken",
      getExchangeBalances: "getExchangeBalances",
      setChatVisible: "setChatVisible",
      sendNotify: "sendNotify",
      getHydraRequestToIssue: "getHydraRequestToIssue",
      getHydraCompanies: "getHydraCompanies",
      setLanguage: "setLanguage",
      initializeUnits: "initializeUnits",
      getProfile: "profile/getProfile",
      patchProfile: "profile/patchProfile",
      setSocketHasPreviousConnection: "socket/setSocketHasPreviousConnection",
      setIsLightTheme: "setIsLightTheme",
      getSystemSettings: "getSystemSettings",
      getSmartContractsTypes: "getSmartContractsTypes",
      getDailyStats: "getDailyStats",
      getTransactions: "getTransactions",
      getInvestments: "capitals/getInvestments"
    }),

    setupCarrot() {
      if (process.env.VUE_APP_PLATFORM === "RWA.Town") {
        return;
      }

      const iframe = document.querySelector("#carrot-frame-bumperCookies");
      if (iframe) {
        iframe.contentDocument.activeElement.style =
          "background: " + (this.isLightTheme ? "#FFFFFF" : "#1C1C1E");
      }

      if (window.carrotquest) {
        window.carrotquest.darkTheme = !this.isLightTheme;
        window.carrotquest.connect("60143-e257a53dd1e3fc524abd3d261a");
      }
    },

    getUserBalanceInterval() {
      this.socketInterval = setInterval(async () => {
        if (this.user) {
          await this.getBalances(this.defaultTokenId);
          await this.getAuOwners();
        }
      }, 30000);
    },

    windowResize() {
      let body = document.body,
        html = document.documentElement;

      let height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      );

      window.parent.postMessage({ height: height + "px" }, "*");
    },

    initWeb3Modal() {
      const projectId = "8a93afa8fa43d9dcf5b589ac25a82eca";

      const ethereum = {
        chainId: 1,
        name: "Ethereum Mainnet",
        currency: "ETH",
        rpcUrl: "https://cloudflare-eth.com",
        explorerUrl: "https://etherscan.io"
      };

      const avax = {
        chainId: 43114,
        name: "Avalanche C-Chain",
        currency: "AVAX",
        rpcUrl: "https://api.avax.network/ext/bc/C/rpc",
        explorerUrl: "https://snowtrace.io"
      };

      const bsc = {
        chainId: 56,
        name: "BNB Smart Chain Mainnet",
        currency: "BNB",
        rpcUrl: "https://bsc-dataseed.binance.org/",
        explorerUrl: "https://bscscan.com/"
      };

      const metadata = {
        name: "Autentic",
        description: "Autentic",
        url: "https://aut.finance/",
        icons: ["https://aut.finance/logo.png"]
      };

      createWeb3Modal({
        ethersConfig: defaultConfig({ metadata }),
        chains: [bsc, avax, ethereum],
        projectId,
        enableAnalytics: true,
        featuredWalletIds: window?.ethereum?.isMetaMask
          ? []
          : ["c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96"]
      });
    },

    async setUserCarrotId(id) {
      const carrotId = this.$cookies.get("carrotquest_uid");
      if (carrotId) {
        try {
          await this.patchProfile({
            id: id,
            params: { carrotId: carrotId }
          });
        }
        catch (e) {
          console.error(e);
        }
      }
    }
  }
};
</script>

<style lang="scss">
.app {
  display: flex;
  width: 100%;
  overflow: hidden;
  background: var(--main-color);

  &__right {
    width: 100%;
    overflow: hidden;
  }

  &__left {
    @media screen and (max-width: 1200px) {
      display: none;
    }
  }
}

.wrapper {
  width: 100%;
  overflow-y: auto;
}
.c-toast-container {
  margin-right: 8px;
}

// Style AuModal
.modal_head_text {
  font-family: "Suisse Intl", serif;
  font-style: normal;
  font-weight: 450;
  font-size: 24px;
  border-bottom: 1px solid var(--color-70);
  margin-bottom: 25px;
  padding-bottom: 5px;
  width: 100%;
  margin-top: -5px;
}
.modal_button {
  display: flex;
  justify-content: center;
  margin-top: 18px;
}
.modal_button_margin {
  margin-right: 10px;
}
</style>
